import React, { useState } from 'react'
import classNames from 'classnames'
import pluralize from 'pluralize'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { useAppDispatch } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'

import { type Task } from '../../../models/MeetingSupport'
import { type User } from '../../../models/User'

import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'

import { AssigneeSelect } from './AssigneeSelect'
import { useTaskMutations } from '../hooks'
import { type TaskListSource } from '../Tasks'
import { isTaskDone } from '../utils'

interface AssignAllTasksLinkProps {
  tasks: Task[]
  onOpen: () => void
}

export const AssignAllTasksLink: React.FC<AssignAllTasksLinkProps> = ({ tasks, onOpen }) => {
  const canAssignAll = tasks.some((task) => !isTaskDone(task))

  return (
    <Tooltip placement='top' title={canAssignAll ? undefined : "All tasks here are completed or synced to your integration, so they can't be moved."}>
      <Button
        className={classNames('text-decoration-none justify-content-start button-medium', canAssignAll ? 'zocks-text-primary' : 'zocks-text-gray-500')}
        variant='link'
        disabled={!canAssignAll}
        onClick={onOpen}
      >
        <i aria-hidden='true' className='bi bi-arrow-left-right me-1' />
        Assign all tasks
      </Button>
    </Tooltip>
  )
}

interface AssignAllTasksFormProps {
  user: User
  tasks: Task[]
  roomId?: string
  trackingSource: TaskListSource
  taskActionCallback?: (tasks: Task[]) => void
  onClose: () => void
}

export const AssignAllTasksForm: React.FC<AssignAllTasksFormProps> = ({ user, tasks, roomId, trackingSource, taskActionCallback, onClose }) => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const { tasks: storedTasks, updateTasks, isUpdatingTasks } = useTaskMutations()

  const [assignee, setAssignee] = useState<User>()

  const openTasks = tasks.filter((task) => !isTaskDone(task))

  const handleAssign = async () => {
    if (!assignee) {
      return
    }
    const updatedTasks = await updateTasks(openTasks.map((task) => ({ id: task.id, assignedTo: { type: 'user', id: assignee.id } })))
    if (!updatedTasks) {
      dispatch(addToast({ variant: 'error', title: "Couldn't move the tasks. Please try again." }))
      return
    }
    dispatch(addToast({ variant: 'success', title: `Moved ${pluralize('task', updatedTasks.length, true)} to ${getUserDisplayName(assignee)}` }))
    trackingClient.track({ eventName: TrackEvent.TASK_REASSIGNED, eventDetails: { method: 'assignAll', source: trackingSource } })
    taskActionCallback?.(storedTasks.map((task) => updatedTasks.find((e) => e.id === task.id) ?? task))
    onClose()
  }

  return (
    <div className='d-flex flex-column gap-3 zocks-bg-color-gray-100 p-3 rounded-1'>
      <AssigneeSelect
        label='Assign all tasks to'
        roomId={roomId}
        unavailableUserId={user.id}
        disabled={isUpdatingTasks}
        autoOpen={true}
        showOpenTasksOnlyHint={openTasks.length < tasks.length}
        onAssigneeSelected={setAssignee}
      />
      <div className='d-flex gap-3 justify-content-end'>
        <Button className='button-small' variant='secondary' onClick={onClose} disabled={isUpdatingTasks}>
          Cancel
        </Button>
        <Button className='button-small' variant='primary' onClick={handleAssign} disabled={!assignee || !openTasks.length} loading={isUpdatingTasks}>
          Assign {pluralize('task', openTasks.length, true)}
        </Button>
      </div>
    </div>
  )
}
